import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { X, FlaskConical } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { FeatureFlag } from '@/constants/featureFlags'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { useMenuVariant, BANNER_DISMISSED_KEY } from '@/hooks/useMenuVariant'

// Pages that only exist in the v1 variant — navigate away when switching back to current
const V1_ONLY_PAGES = ['/periods']

export function MenuVariantBanner() {
  const hasFlag = useFeatureFlag(FeatureFlag.NEW_MENU)
  const { variant, toggle } = useMenuVariant()
  const location = useLocation()
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(BANNER_DISMISSED_KEY) === 'true'
  )

  // Re-show banner when variant changes (toggle resets the dismissed key)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === BANNER_DISMISSED_KEY) {
        setDismissed(e.newValue === 'true')
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  if (!hasFlag || dismissed) return null

  const handleToggle = () => {
    const next = variant === 'current' ? 'v1' : 'current'
    toggle()
    setDismissed(false)
    if (next === 'v1') {
      navigate('/periods')
    } else if (next === 'current' && V1_ONLY_PAGES.includes(location.pathname)) {
      navigate('/salaryreport')
    }
  }

  const handleDismiss = () => {
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true')
    setDismissed(true)
  }

  return (
    <Alert className="rounded-none border-x-0 border-t-0 bg-muted/60 py-2 px-4">
      <FlaskConical className="h-4 w-4 text-muted-foreground" />
      <div className="col-start-2 flex items-center justify-between gap-4">
        <span className="text-sm text-muted-foreground">
          {variant === 'v1'
            ? 'Du provar det nya arbetsflödet.'
            : 'Vill du prova det nya arbetsflödet?'}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleToggle}>
            {variant === 'v1' ? 'Byt tillbaka' : 'Prova nu'}
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleDismiss}>
            <X className="h-4 w-4" />
            <span className="sr-only">Stäng</span>
          </Button>
        </div>
      </div>
    </Alert>
  )
}
